import { Injectable } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { PerfilParticipante } from '../../core/interface/perfil-participante';
import { PerfilRecomendado } from '../../core/interface/perfil-recomendado';
import { CampanhaPerfil } from '../../core/interface/campanha';
import { PerfilInvestidor } from '../../core/interface/perfil-investidor';
import { retry } from 'rxjs';
import { Protocolo } from '../../core/interface/protocolo';
import { QuestionarioRespondido } from '../../core/interface/questionario';

@Injectable({
  providedIn: 'root',
})
export class AppStateService {
  private readonly QUESTIONNAIRE_STORAGE_KEY = 'questionnaireAnswers';
  private readonly SELECTED_PROFILE_KEY = 'selectedProfile';

  private readonly CAMPANHA_KEY = 'campanha';
  private readonly PERFIL_PARTICIPANTE_KEY = 'perfilParticipante';
  private readonly PERFIL_RECOMENDADO_KEY = 'perfilRecomendado';
  private readonly PERFIS_INVESTIDOR_KEY = 'perfisInvestidor';
  private readonly SOLICITACAO_ANDAMENTO_KEY = 'solicitacaoAndamento';
  private readonly PROTOCOLO_KEY = 'protocolo';
  private readonly QUESTIONARIO_ESCOLHA_KEY = 'questionarioEscolha';
  private readonly QUESTIONARIO_RESPONDIDO_KEY = 'questionarioRespondido';
  private readonly TOKEN_KEY = 'sToken';
  private readonly MENSAGEM_NAO_APTO_KEY = 'mensagemNaoApto';

  private questionnaireAnswers: Record<number, number | undefined> = {};
  private selectedProfile?: number;
  private perfilParticipante?: PerfilParticipante;
  private perfilRecomendado?: PerfilRecomendado;
  private campanha?: CampanhaPerfil;
  private perfisInvestidor?: PerfilInvestidor[];
  private solicitacaoAndamento?: boolean;
  private protocolo?: Protocolo;
  private questionarioEscolhaParticipante?: QuestionarioRespondido[];
  private questionarioRespondidoParticipante?: QuestionarioRespondido[];
  private token ?: string = '';
  private mensagemNaoApto?: string;

  constructor(
    private localStorageService: LocalStorageService
  ) { }

  // Token 
  setToken(token: string): void {
    this.token = token;
    this.localStorageService.add(
      this.TOKEN_KEY,
      token
    );
  }

  getToken(): string | undefined {
    if(this.token){
      return this.token;
    }

    const tokenStorage = this.localStorageService.get(this.TOKEN_KEY);

    if (tokenStorage) {
      this.token = tokenStorage;
      return this.token;
    }

    return undefined;
  }
  // MENSAGEM NAO APTO (detail retornado pela API)

  setMensagemNaoApto(mensagem: string): void {
    this.mensagemNaoApto = mensagem;

    this.localStorageService.add(
      this.MENSAGEM_NAO_APTO_KEY,
      mensagem
    );
  }

  getMensagemNaoApto(): string | undefined {

    if (this.mensagemNaoApto) {
      return this.mensagemNaoApto;
    }

    const mensagemStorage =
      this.localStorageService.get(this.MENSAGEM_NAO_APTO_KEY);

    if (mensagemStorage) {

      this.mensagemNaoApto = mensagemStorage;

      return this.mensagemNaoApto;
    }

    return undefined;
  }

  clearMensagemNaoApto(): void {

    this.mensagemNaoApto = undefined;

    this.localStorageService.removeItem(
      this.MENSAGEM_NAO_APTO_KEY
    );
  }

  // CAMPANHA

  setCampanha(campanha: CampanhaPerfil): void {

    this.campanha = campanha;

    this.localStorageService.add(
      this.CAMPANHA_KEY,
      JSON.stringify(campanha)
    );
  }

  getCampanha(): CampanhaPerfil | undefined {

    if (this.campanha) {
      return this.campanha;
    }

    const campanhaStorage =
      this.localStorageService.get(this.CAMPANHA_KEY);

    if (campanhaStorage) {

      this.campanha = JSON.parse(campanhaStorage);

      return this.campanha;
    }

    return undefined;
  }

  // QUESTIONÁRIO

  getQuestionnaireAnswers(): Record<number, number | undefined> {

    if (Object.keys(this.questionnaireAnswers).length > 0) {
      return { ...this.questionnaireAnswers };
    }

    const answersStorage =
      this.localStorageService.get(
        this.QUESTIONNAIRE_STORAGE_KEY
      );

    if (answersStorage) {

      this.questionnaireAnswers =
        JSON.parse(answersStorage);

      return { ...this.questionnaireAnswers };
    }

    return {};
  }

  setQuestionnaireAnswers(
    answers: Record<number, number | undefined>
  ): void {

    this.questionnaireAnswers = { ...answers };

    this.localStorageService.add(
      this.QUESTIONNAIRE_STORAGE_KEY,
      JSON.stringify(this.questionnaireAnswers)
    );
  }

  // PERFIL SELECIONADO

  getSelectedProfile(): number | undefined {

    if (this.selectedProfile !== undefined) {
      return this.selectedProfile;
    }

    const profileStorage =
      this.localStorageService.get(
        this.SELECTED_PROFILE_KEY
      );

    if (profileStorage) {

      this.selectedProfile =
        Number(profileStorage);

      return this.selectedProfile;
    }

    return undefined;
  }

  setSelectedProfile(profileId: number): void {

    this.selectedProfile = profileId;

    this.localStorageService.add(
      this.SELECTED_PROFILE_KEY,
      profileId.toString()
    );
  }

  // PERFIS INVESTIDOR

  setPerfisInvestimento(
    perfis: PerfilInvestidor[]
  ): void {

    this.perfisInvestidor = perfis;

    this.localStorageService.add(
      this.PERFIS_INVESTIDOR_KEY,
      JSON.stringify(perfis)
    );
  }

  getPerfisInvestimento(): PerfilInvestidor[] | undefined {

    if (this.perfisInvestidor) {
      return this.perfisInvestidor;
    }

    const perfisStorage =
      this.localStorageService.get(
        this.PERFIS_INVESTIDOR_KEY
      );

    if (perfisStorage) {

      this.perfisInvestidor =
        JSON.parse(perfisStorage);

      return this.perfisInvestidor;
    }

    return [];
  }

  // PERFIL PARTICIPANTE

  setPerfilParticipante(
    perfil: PerfilParticipante
  ): void {

    this.perfilParticipante = perfil;

    this.localStorageService.add(
      this.PERFIL_PARTICIPANTE_KEY,
      JSON.stringify(perfil)
    );
  }

  getPerfilParticipante():
    PerfilParticipante | undefined {

    if (this.perfilParticipante) {
      return this.perfilParticipante;
    }

    const perfilStorage =
      this.localStorageService.get(
        this.PERFIL_PARTICIPANTE_KEY
      );

    if (perfilStorage) {

      this.perfilParticipante =
        JSON.parse(perfilStorage);

      return this.perfilParticipante;
    }

    return undefined;
  }

  // PERFIL RECOMENDADO

  setPerfilRecomendado(
    perfil: PerfilRecomendado
  ): void {

    this.perfilRecomendado = perfil;

    this.localStorageService.add(
      this.PERFIL_RECOMENDADO_KEY,
      JSON.stringify(perfil)
    );
  }

  getPerfilRecomendado():
    PerfilRecomendado | undefined {

    if (this.perfilRecomendado) {
      return this.perfilRecomendado;
    }

    const perfilStorage =
      this.localStorageService.get(
        this.PERFIL_RECOMENDADO_KEY
      );

    if (perfilStorage) {

      this.perfilRecomendado =
        JSON.parse(perfilStorage);

      return this.perfilRecomendado;
    }

    return undefined;
  }

  setSolicitacaoAndamento(solicitacao: boolean): void {
    this.solicitacaoAndamento = solicitacao;

    this.localStorageService.add(
      this.SOLICITACAO_ANDAMENTO_KEY,
      solicitacao
    );
  }

  getSolicitacaoAndamento(): boolean {
    if (this.solicitacaoAndamento) {
      return this.solicitacaoAndamento;
    }

    return false
  }

  //  PROTOCOLO
  setProtocolo(protocolo: Protocolo): void {
    this.protocolo = protocolo;

    this.localStorageService.add(
      this.PROTOCOLO_KEY,
      JSON.stringify(protocolo)
    );
  }

  getProtocolo(): Protocolo | undefined {
    if (this.protocolo) {
      return this.protocolo;
    }

    return undefined
  }

  // QUESTIONARIO ESCOLHA DAS RESPOSTAS DO QUESTINARIO DO PARTICIPANTE
  setEscolhaParticipante(
    questionarioRespondido: QuestionarioRespondido[]
  ): void {

    this.questionarioEscolhaParticipante = questionarioRespondido;

    this.localStorageService.add(
      this.QUESTIONARIO_ESCOLHA_KEY,
      JSON.stringify(questionarioRespondido)
    );
  }

  getEscolhaParticipante(): QuestionarioRespondido[] | undefined {

    if (this.questionarioEscolhaParticipante) {
      return this.questionarioEscolhaParticipante;
    }

    const questionarioStorage =
      this.localStorageService.get(
        this.QUESTIONARIO_ESCOLHA_KEY
      );

    if (questionarioStorage) {

      this.questionarioEscolhaParticipante =
        JSON.parse(questionarioStorage);

      return this.questionarioEscolhaParticipante;
    }

    return [];
  }

  // QUESTIONARIO RESPOSTA QUE O PARTICIPANTE RESPONDEU PARA O QUESTIONARIO
  setQuestionarioRespondidoParticipante(
    questionarioRespondido: QuestionarioRespondido[]
  ): void {

    this.questionarioRespondidoParticipante = questionarioRespondido;

    this.localStorageService.add(
      this.QUESTIONARIO_RESPONDIDO_KEY,
      JSON.stringify(questionarioRespondido)
    );
  }

  getQuestionarioRespondidoParticipante(): QuestionarioRespondido[] | undefined {

    if (this.questionarioRespondidoParticipante) {
      return this.questionarioRespondidoParticipante;
    }

    const questionarioStorage =
      this.localStorageService.get(
        this.QUESTIONARIO_RESPONDIDO_KEY
      );

    if (questionarioStorage) {

      this.questionarioRespondidoParticipante =
        JSON.parse(questionarioStorage);

      return this.questionarioRespondidoParticipante;
    }

    return [];
  }

  // CLEAR

  clearToken(): void {

    this.token = undefined;
    
    this.localStorageService.removeItem(
      this.TOKEN_KEY
    );
  }

  clearState(): void {

    this.questionnaireAnswers = {};

    this.selectedProfile = undefined;

    this.perfilParticipante = undefined;

    this.perfilRecomendado = undefined;

    this.campanha = undefined;

    this.perfisInvestidor = undefined;

    this.solicitacaoAndamento = undefined;

    this.protocolo = undefined;

    this.questionarioEscolhaParticipante = undefined;

    this.questionarioRespondidoParticipante = undefined;

    this.token = undefined;

    this.mensagemNaoApto = undefined;

    this.localStorageService.removeItem(
      this.QUESTIONNAIRE_STORAGE_KEY
    );

    this.localStorageService.removeItem(
      this.SELECTED_PROFILE_KEY
    );

    this.localStorageService.removeItem(
      this.CAMPANHA_KEY
    );

    this.localStorageService.removeItem(
      this.PERFIL_PARTICIPANTE_KEY
    );

    this.localStorageService.removeItem(
      this.PERFIL_RECOMENDADO_KEY
    );

    this.localStorageService.removeItem(
      this.PERFIS_INVESTIDOR_KEY
    );

    this.localStorageService.removeItem(
      this.SOLICITACAO_ANDAMENTO_KEY
    );

    this.localStorageService.removeItem(
      this.PROTOCOLO_KEY
    );

    this.localStorageService.removeItem(
      this.QUESTIONARIO_ESCOLHA_KEY
    );

    this.localStorageService.removeItem(
      this.QUESTIONARIO_RESPONDIDO_KEY
    );

    this.localStorageService.removeItem(
      this.TOKEN_KEY
    );

    this.localStorageService.removeItem(
      this.MENSAGEM_NAO_APTO_KEY
    );
  }
}